import { useEffect, useState } from "react";
import type { AbstractAgent } from "@ag-ui/client";

export interface RunError {
  message: string;
  // AG-UI run_id (== backend trace_id) of the failed run, captured at
  // RunStarted since RunErrorEvent doesn't carry it. Quoted to support.
  runId?: string;
}

// The message from a terminal RUN_ERROR, held at conversation level rather than
// attached to a turn. A failed run persists nothing, so the turn it failed on
// can be erased by the post-run reconcile — an error keyed to a message id would
// go with it. One standalone value survives that. `useAgent` only wires
// onRunErrorEvent to end the run; without this the message is dropped entirely.
//
// Cleared on RunStarted, so only the latest failure is ever shown.
export function useRunError(agent: AbstractAgent): RunError | null {
  const [error, setError] = useState<RunError | null>(null);

  useEffect(() => {
    let runId: string | undefined;
    const sub = agent.subscribe({
      onRunStartedEvent: ({ event }) => {
        runId = event.runId;
        setError((prev) => (prev === null ? prev : null));
      },
      onRunErrorEvent: ({ event }: { event: { message?: string } }) => {
        const message = event.message?.trim();
        if (!message) return;
        // Cancellation ends the run through its own "[cancelled]" marker, not a
        // RUN_ERROR — but guard defensively so a cancel never reads as a failure.
        if (message.toLowerCase().includes("cancel")) return;
        setError({ message, runId });
      },
    });
    return () => sub.unsubscribe();
  }, [agent]);

  return error;
}
